import { api } from './api';

export interface Media {
  id: string;
  title: string;
  originalName: string;
  key: string;
  url?: string;
  mimeType: string;
  size: number;
  folder?: string;
  status: 'processing' | 'ready' | 'error';
  gumletAssetId?: string;
  thumbnailUrl?: string;
  duration?: number;
  qualities?: Array<{ quality: string; url: string }>;
  createdAt: string;
}

export interface Attachment {
  id: string;
  lessonId: string;
  name: string;
  originalName: string;
  url: string;
  key: string;
  mimeType: string;
  size: number;
  downloads: number;
  createdAt: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

class MediaService {
  async uploadImage(
    file: File,
    folder?: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<{ id: string; url: string; key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) {
      formData.append('folder', folder);
    }

    const { data } = await api.post('/media/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            ),
          });
        }
      },
    });

    return data;
  }

  async uploadVideo(
    file: File,
    title: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<{ id: string; gumletAssetId: string; status: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);

    const { data } = await api.post('/media/upload/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // Disable timeout for large videos
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            ),
          });
        }
      },
    });

    return data;
  }

  async uploadVideoDirect(
    file: File,
    title?: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<{ id: string; url: string; key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (title) {
      formData.append('title', title);
    }

    const { data } = await api.post('/media/upload/video-direct', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            ),
          });
        }
      },
    });

    return data?.data || data;
  }

  async uploadAttachment(
    lessonId: string,
    file: File,
    name?: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);
    if (name) {
      formData.append('name', name);
    }

    const { data } = await api.post(
      `/media/upload/attachment/${lessonId}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              ),
            });
          }
        },
      },
    );

    return data;
  }

  async getMedia(id: string): Promise<Media> {
    const { data } = await api.get(`/media/${id}`);
    return data;
  }

  async deleteMedia(id: string): Promise<void> {
    await api.delete(`/media/${id}`);
  }

  async getAttachmentDownloadUrl(
    attachmentId: string,
  ): Promise<{ url: string; filename: string }> {
    const { data } = await api.get(
      `/media/attachment/${attachmentId}/download`,
    );
    return data;
  }

  async deleteAttachment(attachmentId: string): Promise<void> {
    await api.delete(`/media/attachment/${attachmentId}`);
  }

  /**
   * Gerar URL do player Gumlet para embed
   */
  getGumletPlayerUrl(assetId: string): string {
    return `https://play.gumlet.io/embed/${assetId}`;
  }

  /**
   * Formatar tamanho do arquivo
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Validar tipo de arquivo de imagem
   */
  isValidImageType(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    return validTypes.includes(file.type);
  }

  /**
   * Validar tipo de arquivo de vídeo
   */
  isValidVideoType(file: File): boolean {
    const validTypes = [
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'video/x-msvideo',
    ];
    return validTypes.includes(file.type);
  }
}

export const mediaService = new MediaService();
